<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title v-if="company">
            {{ company.name }}
        </template>

        <template v-slot:header v-if="company">
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabCompanyContentMain" id="tabCompanyLinkMain" data-toggle="tab" role="tab" aria-controls="tabCompanyContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content" v-if="company">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabCompanyContentMain" role="tabpanel" aria-labelledby="tabCompanyLinkMain">
                    <!-- Company Information widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Company Information</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editCompany">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="d-flex flex-row">
                                    <div class="mui-textfield col-6 px-0">
                                        <span class="textfield">
                                            {{ company.company_code }}
                                        </span>
                                        <label>Company #</label>
                                    </div>
                                    <div class="mui-textfield col-6 px-0">
                                        <span class="textfield">
                                            {{ company.EIN }}
                                        </span>
                                        <label>EIN</label>
                                    </div>
                                </div>

                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ company.name }}
                                    </span>
                                    <label>Legal Name</label>
                                </div>
                                <div class="mui-textfield" v-if="company.known_as">
                                    <span class="textfield">
                                        {{ company.known_as }}
                                    </span>
                                    <label>Friendly Name</label>
                                </div>
                                <div class="mui-textfield" v-if="company.dba">
                                    <span class="textfield">
                                        {{ company.dba }}
                                    </span>
                                    <label>Doing Business As</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        <template v-if="company.address_1">{{ company.address_1 }} {{ company.address_2 }}<br/></template>
                                        <template v-if="company.city">{{ company.city }}, </template>{{ company.state }} {{ company.postal_code }}
                                    </span>
                                    <label>Company Address</label>
                                </div>
                                <div class="mui-textfield" v-if="company.show_clocking_id && client.clocking_id_enabled">
                                    <span class="textfield">Yes</span>
                                    <label>Show Clocking ID</label>
                                </div>
                                <div class="mui-textfield" v-if="company.franchisee_code">
                                    <span class="textfield">
                                        {{ company.franchisee_code }}
                                    </span>
                                    <label>Franchisee Code</label>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Locations widget -->
                    <info-widget :collapsible="true" :is-expanded="locationsExpanded" @toggle_expand="locationsExpanded = !locationsExpanded">
                        <template v-slot:title>Locations ({{ company.work_locations?.length || 0 }})</template>

                        <slot>
                            <div v-if="company.work_locations?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th class="text-right">Employees</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="location in company.work_locations">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ location.name }}</span>
                                            </td>
                                            <td class="text-right">
                                                <span>{{ location.num_employees }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This company does not have any locations yet.
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Positions widget -->
                    <info-widget :collapsible="true" :is-expanded="positionsExpanded" @toggle_expand="positionsExpanded = !positionsExpanded">
                        <template v-slot:title>Positions ({{ activePositions.length || 0 }})</template>
                        <slot>
                            <div v-if="activePositions.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th class="cw-toggle"></th>
                                            <th>Positions</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="position in activePositions" :key="position.id">
                                        <tr>
                                            <td  class="cw-toggle position-relative">
                                                <a href class="widget-toggle-wrapper collapse-trigger" role="button" :class="{'is-expanded': showPositionDetails[position.id]}" @click.prevent="togglePositionDetails(position.id)">
                                                    <collapse-toggle-button />
                                                </a>
                                            </td>
                                            <td class="truncate-wrapper">
                                                <span>{{ position.terminal_value }}</span>
                                            </td>
                                        </tr>
                                        <tr class="child no-transition" v-if="showPositionDetails[position.id]">
                                            <td colspan="2">
                                                <div class="card card-body item-details">
                                                    # of Active Employees: {{ position.num_employees }}
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This company does not have any positions yet.
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Earnings widget -->
                    <info-widget :collapsible="true" :is-expanded="earningsExpanded" @toggle_expand="earningsExpanded = !earningsExpanded">
                        <template v-slot:title>Earnings ({{ company.company_earning_type?.length || 0 }})</template>
                        <slot>
                            <div v-if="company.company_earning_type?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th class="text-right">Type</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="earning_type in company.company_earning_type">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ earning_type.name }}</span>
                                            </td>
                                            <td class="text-right">
                                                <span>{{ earning_type.type_pretty }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else class="info-widget-empty p-2">
                                This company does not have any earnings yet.
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Deductions widget -->
                    <info-widget :collapsible="true" :is-expanded="deductionsExpanded" @toggle_expand="deductionsExpanded = !deductionsExpanded">
                        <template v-slot:title>Deductions ({{ company.company_deduction_type?.length || 0 }})</template>
                        <slot>
                            <div v-if="company.company_deduction_type?.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Deductions</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="deduction_type in company.company_deduction_type">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ deduction_type.name }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else class="info-widget-empty p-2">
                                This company does not have any deductions yet.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-company-modal v-if="company" ref="editCompanyModal" :company="company" :client="client" @updated="onUpdate" @close="editCompanyModalOpen = false" />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditCompanyModal from './EditCompanyModal'

export default {
    props: ['companyId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditCompanyModal},
    computed: {
        activePositions() {
            let ret = []
            if (this.company?.positions) {
                ret = this.company.positions.filter(p => p.is_active)
            }
            return ret
        }
    },
    data() {

        return {
            company: null,
            activeTab: this.tab || 'main',
            editCompanyModalOpen: false,
            locationsExpanded: true,
            positionsExpanded: true,
            earningsExpanded: true,
            deductionsExpanded: true,
            showLocationDetails: {},
            showPositionDetails: {},
        }
    },
    watch: {
        companyId() {
            this.getCompany()
        }
    },
    mounted() {
        this.getCompany()
    },
    methods: {
        getCompany() {
            this.company = null
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/companies/${this.companyId}`).then(resp => {
                this.company = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        editCompany() {
            this.editCompanyModalOpen = true
            this.$refs.editCompanyModal.open()
        },
        onUpdate(company) {
            if (company) {
                Object.assign(this.company, company)
            }
            this.$emit('updated')
        },
        toggleLocationDetails(locationId) {
            this.showLocationDetails[locationId] = !this.showLocationDetails[locationId]
        },
        togglePositionDetails(positionId) {
            this.showPositionDetails[positionId] = !this.showPositionDetails[positionId]
        },
    },
}
</script>
